import { buildRestaurantBrandTheme, normalizeRestaurantBranding } from "./brandTheme";
import { normalizeQrMenuStyle } from "./qrMenuStyle";
import { normalizeQrCategoryOrder, normalizeQrSubcategoryOrder } from "./qrCategoryOrder";
import {
  DEFAULT_BRANCH_COMMISSION_RATE,
  DEFAULT_BRANCH_PAYMENT_PROVIDER,
  DEFAULT_BRANCH_PAYMENT_PROVIDER_STATUS,
  DEFAULT_BRANCH_PAYMENT_SPLIT_MODE,
  DEFAULT_KIOSK_PAYMENT_METHODS,
  DEFAULT_QR_PAYMENT_METHODS,
  normalizeCommissionRate,
  normalizePaymentMethods,
  normalizePaymentProvider,
  normalizePaymentProviderStatus,
  normalizePaymentSplitMode,
  resolveBranchChannelCommissionRates,
} from "./paymentSettings";

const STORAGE_PREFIX = "mozzoapp:restaurant-profile:";
const STATE_STORAGE_PREFIX = "mozzoapp:restaurant-profile-state:";
export const RESTAURANT_PROFILE_UPDATED_EVENT = "mozzoapp:restaurant-profile-updated";

export const DIAS_SEMANA = [
  { id: "lunes", label: "Lunes" },
  { id: "martes", label: "Martes" },
  { id: "miercoles", label: "Miercoles" },
  { id: "jueves", label: "Jueves" },
  { id: "viernes", label: "Viernes" },
  { id: "sabado", label: "Sabado" },
  { id: "domingo", label: "Domingo" },
];

export const KITCHEN_TICKET_PAPER_OPTIONS = [
  {
    id: "58mm",
    label: "58 mm",
    description: "Ideal para comandera angosta o tickets compactos.",
  },
  {
    id: "80mm",
    label: "80 mm",
    description: "El formato mas comun para comandas termicas de cocina.",
  },
];

export const normalizeKitchenTicketPaper = (value) =>
  value === "58mm" ? "58mm" : "80mm";

export const normalizeServiceMode = (value) =>
  value === "kiosk" ? "kiosk" : "salon";

export const normalizeKioskMenuMode = (value) =>
  value === "custom" ? "custom" : "current";

export const normalizeOperationalFeatureEnabled = (value) => value !== false;

const normalizeProfileText = (value) =>
  value === undefined || value === null ? "" : `${value}`.trim();

const looksLikeMercadoPagoAppReference = (value) =>
  /^APP_USR-/i.test(normalizeProfileText(value));

const sanitizePaymentAccountReference = (value) =>
  looksLikeMercadoPagoAppReference(value) ? "" : normalizeProfileText(value);

const readLegacyBranchSetting = (restaurant, camelKey, snakeKey) => {
  if (!restaurant || typeof restaurant !== "object") {
    return undefined;
  }

  if (restaurant[camelKey] !== undefined) {
    return restaurant[camelKey];
  }

  return snakeKey ? restaurant[snakeKey] : undefined;
};

const REMOTE_OPERATIONAL_PROFILE_KEYS = Object.freeze([
  "kitchenMode",
  "serviceMode",
  "salonEnabled",
  "deliveryEnabled",
  "operationalSetupCompleted",
  "stockEnabled",
  "waiterEnabled",
  "aiEnabled",
  "kioskDineInImageUrl",
  "kioskTakeawayImageUrl",
  "kioskMenuMode",
  "kioskStationCount",
  "paymentProvider",
  "paymentProviderStatus",
  "paymentAccountReference",
  "paymentAccountCbu",
  "paymentAccountAlias",
  "paymentLocationCity",
  "paymentLocationState",
  "paymentLocationLatitude",
  "paymentLocationLongitude",
  "paymentLocationReference",
  "kioskNativeQrReady",
  "commissionRate",
  "manualOrderCommissionRate",
  "menuQrCommissionRate",
  "kioskCommissionRate",
  "iaCommissionRate",
  "paymentSplitMode",
  "kioskPaymentMethods",
  "qrPaymentMethods",
]);

const stripRemoteOperationalProfileFields = (value, remoteBranchProfile) => {
  const nextValue = sanitizeStoredProfile(value);

  if (
    !remoteBranchProfile ||
    !nextValue ||
    typeof nextValue !== "object" ||
    Array.isArray(nextValue)
  ) {
    return nextValue;
  }

  const sanitized = { ...nextValue };
  REMOTE_OPERATIONAL_PROFILE_KEYS.forEach((key) => {
    delete sanitized[key];
  });

  return sanitized;
};

const sanitizeStoredProfile = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return value && typeof value === "object" ? value : {};
  }

  return {
    ...value,
    paymentAccountReference: sanitizePaymentAccountReference(
      value.paymentAccountReference
    ),
  };
};

export const COVER_DISPLAY_MODE_OPTIONS = [
  {
    id: "centered",
    label: "Centrada",
    description: "Muestra la imagen completa, centrada, con fondo desenfocado.",
  },
  {
    id: "stretch",
    label: "Estirada",
    description: "Rellena todo el box ajustando la imagen al ancho y al alto.",
  },
  {
    id: "normal",
    label: "Normal",
    description: "Usa el comportamiento clasico de portada, llenando sin deformar.",
  },
];

export const normalizeCoverDisplayMode = (value) => {
  if (value === "centered" || value === "normal") {
    return value;
  }

  return "stretch";
};

export const normalizeKitchenModeForServiceMode = (serviceMode, kitchenMode) => {
  if (normalizeServiceMode(serviceMode) === "kiosk") {
    return kitchenMode === "screen" || kitchenMode === "printer" ? kitchenMode : "screen";
  }

  return kitchenMode === "screen" || kitchenMode === "printer" ? kitchenMode : "manual";
};

export const normalizeKioskStationCount = (value) => {
  const parsed = Number(value);

  if (!Number.isFinite(parsed)) {
    return 1;
  }

  return Math.min(12, Math.max(1, Math.round(parsed)));
};

const createFranja = (apertura = "08:00", cierre = "00:00") => ({
  apertura,
  cierre,
});

export const createDefaultHorarioDetallado = () =>
  DIAS_SEMANA.map((dia) => ({
    id: dia.id,
    label: dia.label,
    abierto: true,
    franjas: [createFranja()],
  }));

const normalizeFranja = (franja, fallback) => ({
  apertura: franja?.apertura || fallback.apertura,
  cierre: franja?.cierre || fallback.cierre,
});

export const normalizeHorarioDetallado = (horarioDetallado) => {
  const incoming = Array.isArray(horarioDetallado) ? horarioDetallado : [];

  return DIAS_SEMANA.map((dia, index) => {
    const found = incoming.find((item) => item?.id === dia.id);
    const abierto = found?.abierto !== false;
    const defaultFranja = index < 5 ? createFranja("08:00", "00:00") : createFranja("09:00", "00:00");
    const franjas = abierto
      ? (Array.isArray(found?.franjas) && found.franjas.length > 0
          ? found.franjas.slice(0, 2).map((franja) => normalizeFranja(franja, defaultFranja))
          : [defaultFranja])
      : [];

    return {
      id: dia.id,
      label: dia.label,
      abierto,
      franjas,
    };
  });
};

export const formatHorarioDia = (dia) => {
  if (!dia?.abierto) {
    return "Cerrado";
  }

  const franjas = Array.isArray(dia?.franjas) ? dia.franjas : [];
  if (franjas.length === 0) {
    return "Sin horario";
  }

  return franjas.map((franja) => `${franja.apertura} a ${franja.cierre}`).join(" / ");
};

const buildRangoDias = (startIndex, endIndex) => {
  const startLabel = DIAS_SEMANA[startIndex].label;
  const endLabel = DIAS_SEMANA[endIndex].label.toLowerCase();

  if (startIndex === endIndex) {
    return startLabel;
  }

  return `${startLabel} a ${endLabel}`;
};

export const buildHorarioResumen = (horarioDetallado) => {
  const normalized = normalizeHorarioDetallado(horarioDetallado);
  const grupos = [];

  normalized.forEach((dia, index) => {
    const horarioTexto = formatHorarioDia(dia);
    const ultimoGrupo = grupos[grupos.length - 1];

    if (ultimoGrupo && ultimoGrupo.horarioTexto === horarioTexto && ultimoGrupo.endIndex === index - 1) {
      ultimoGrupo.endIndex = index;
      return;
    }

    grupos.push({
      horarioTexto,
      startIndex: index,
      endIndex: index,
    });
  });

  return grupos
    .map((grupo) => {
      const rango = buildRangoDias(grupo.startIndex, grupo.endIndex);
      if (grupo.horarioTexto === "Cerrado") {
        return `${rango} cerrado`;
      }

      return `${rango} ${grupo.horarioTexto}`;
    })
    .join(" · ");
};

export const buildRestaurantProfileStorageKey = (restaurantId) =>
  `${STORAGE_PREFIX}${restaurantId || "default"}`;

const buildRestaurantProfileStateStorageKey = (restaurantId) =>
  `${STATE_STORAGE_PREFIX}${restaurantId || "default"}`;

const readStorageObject = (storage, key, errorLabel) => {
  if (!storage) {
    return {};
  }

  try {
    const rawValue = storage.getItem(key);
    return rawValue ? sanitizeStoredProfile(JSON.parse(rawValue) || {}) : {};
  } catch (error) {
    console.error(errorLabel, error);
    return {};
  }
};

const createDefaultProfile = (restaurant) => {
  const horarioDetallado = createDefaultHorarioDetallado();

  return {
    nombre: restaurant?.name || "MozzoApp",
    telefono: restaurant?.phone || "",
    direccion: restaurant?.address || "",
    imagenPortada: "",
    coverDisplayMode: "stretch",
    kioskDineInImageUrl: "",
    kioskTakeawayImageUrl: "",
    descripcion:
      restaurant?.description ||
      "Un espacio pensado para que cada comensal viva la identidad del restaurante desde la carta hasta la mesa.",
    horarioDetallado,
    horario: buildHorarioResumen(horarioDetallado),
    caracteristicas: ["Familiar", "Take away", "Salon"],
    mensajeBienvenida:
      "Bienvenidos. Queremos que cada pedido se sienta claro, rico y facil de elegir.",
    kitchenMode: normalizeKitchenModeForServiceMode("salon", "manual"),
    kitchenTicketPaper: "80mm",
    serviceMode: "salon",
    salonEnabled: true,
    deliveryEnabled: false,
    wePedidosEnabled: false,
    operationalSetupCompleted: false,
    stockEnabled: true,
    waiterEnabled: true,
    aiEnabled: true,
    kioskMenuMode: "current",
    qrMenuStyle: "classic",
    qrCategoryOrder: [],
    qrSubcategoryOrder: {},
    kioskStationCount: 1,
    paymentProvider: DEFAULT_BRANCH_PAYMENT_PROVIDER,
    paymentProviderStatus: DEFAULT_BRANCH_PAYMENT_PROVIDER_STATUS,
    paymentAccountReference: "",
    paymentAccountCbu: "",
    paymentAccountAlias: "",
    paymentLocationCity: "",
    paymentLocationState: "",
    paymentLocationLatitude: "",
    paymentLocationLongitude: "",
    paymentLocationReference: "",
    kioskNativeQrReady: false,
    commissionRate: DEFAULT_BRANCH_COMMISSION_RATE,
    manualOrderCommissionRate: DEFAULT_BRANCH_COMMISSION_RATE,
    menuQrCommissionRate: DEFAULT_BRANCH_COMMISSION_RATE,
    kioskCommissionRate: DEFAULT_BRANCH_COMMISSION_RATE,
    iaCommissionRate: DEFAULT_BRANCH_COMMISSION_RATE,
    paymentSplitMode: DEFAULT_BRANCH_PAYMENT_SPLIT_MODE,
    kioskPaymentMethods: [...DEFAULT_KIOSK_PAYMENT_METHODS],
    qrPaymentMethods: [...DEFAULT_QR_PAYMENT_METHODS],
    branding: normalizeRestaurantBranding(),
  };
};

const extractRemoteBranchProfileFromRestaurant = (restaurant) => {
  if (!restaurant || typeof restaurant !== "object") {
    return null;
  }

  const kitchenMode = readLegacyBranchSetting(
    restaurant,
    "kitchenMode",
    "kitchen_mode"
  );
  const serviceMode = readLegacyBranchSetting(
    restaurant,
    "serviceMode",
    "service_mode"
  );
  const salonEnabled = readLegacyBranchSetting(
    restaurant,
    "salonEnabled",
    "salon_enabled"
  );
  const deliveryEnabled = readLegacyBranchSetting(
    restaurant,
    "deliveryEnabled",
    "delivery_enabled"
  );
  const operationalSetupCompleted = readLegacyBranchSetting(
    restaurant,
    "operationalSetupCompleted",
    "operational_setup_completed"
  );
  const stockEnabled = readLegacyBranchSetting(
    restaurant,
    "stockEnabled",
    "stock_enabled"
  );
  const waiterEnabled = readLegacyBranchSetting(
    restaurant,
    "waiterEnabled",
    "waiter_enabled"
  );
  const aiEnabled = readLegacyBranchSetting(restaurant, "aiEnabled", "ai_enabled");
  const coverDisplayMode = readLegacyBranchSetting(
    restaurant,
    "coverDisplayMode",
    "cover_display_mode"
  );
  const kioskDineInImageUrl = readLegacyBranchSetting(
    restaurant,
    "kioskDineInImageUrl",
    "kiosk_dine_in_image_url"
  );
  const kioskTakeawayImageUrl = readLegacyBranchSetting(
    restaurant,
    "kioskTakeawayImageUrl",
    "kiosk_takeaway_image_url"
  );
  const kioskMenuMode = readLegacyBranchSetting(
    restaurant,
    "kioskMenuMode",
    "kiosk_menu_mode"
  );
  const kioskStationCount = readLegacyBranchSetting(
    restaurant,
    "kioskStationCount",
    "kiosk_station_count"
  );
  const paymentProvider = readLegacyBranchSetting(
    restaurant,
    "paymentProvider",
    "payment_provider"
  );
  const paymentProviderStatus = readLegacyBranchSetting(
    restaurant,
    "paymentProviderStatus",
    "payment_provider_status"
  );
  const paymentAccountReference = sanitizePaymentAccountReference(
    readLegacyBranchSetting(
      restaurant,
      "paymentAccountReference",
      "payment_account_reference"
    )
  );
  const paymentAccountCbu = readLegacyBranchSetting(
    restaurant,
    "paymentAccountCbu",
    "payment_account_cbu"
  );
  const paymentAccountAlias = readLegacyBranchSetting(
    restaurant,
    "paymentAccountAlias",
    "payment_account_alias"
  );
  const paymentLocationCity = readLegacyBranchSetting(
    restaurant,
    "paymentLocationCity",
    "payment_location_city"
  );
  const paymentLocationState = readLegacyBranchSetting(
    restaurant,
    "paymentLocationState",
    "payment_location_state"
  );
  const paymentLocationLatitude = readLegacyBranchSetting(
    restaurant,
    "paymentLocationLatitude",
    "payment_location_latitude"
  );
  const paymentLocationLongitude = readLegacyBranchSetting(
    restaurant,
    "paymentLocationLongitude",
    "payment_location_longitude"
  );
  const paymentLocationReference = readLegacyBranchSetting(
    restaurant,
    "paymentLocationReference",
    "payment_location_reference"
  );
  const kioskNativeQrReady = readLegacyBranchSetting(
    restaurant,
    "kioskNativeQrReady",
    "kiosk_native_qr_ready"
  );
  const commissionRate = readLegacyBranchSetting(
    restaurant,
    "commissionRate",
    "commission_rate"
  );
  const paymentSplitMode = readLegacyBranchSetting(
    restaurant,
    "paymentSplitMode",
    "payment_split_mode"
  );
  const kioskPaymentMethods = readLegacyBranchSetting(
    restaurant,
    "kioskPaymentMethods",
    "kiosk_payment_methods"
  );
  const qrPaymentMethods = readLegacyBranchSetting(
    restaurant,
    "qrPaymentMethods",
    "qr_payment_methods"
  );
  const channelRates = resolveBranchChannelCommissionRates(restaurant);

  const hasRemoteSettings =
    readLegacyBranchSetting(restaurant, "qrMenuStyle", "qr_menu_style") !== undefined ||
    readLegacyBranchSetting(restaurant, "qrCategoryOrder", "qr_category_order") !== undefined ||
    readLegacyBranchSetting(restaurant, "qrSubcategoryOrder", "qr_subcategory_order") !== undefined ||
    Boolean(restaurant.logoUrl) ||
    Boolean(restaurant.logoCompactUrl) ||
    Boolean(restaurant.iconLogoUrl) ||
    Boolean(restaurant.loadingLogoUrl) ||
    Boolean(restaurant.coverUrl) ||
    Boolean(restaurant.primaryColor) ||
    Boolean(restaurant.secondaryColor) ||
    Boolean(restaurant.fontFamily) ||
    Boolean(restaurant.welcomeMessage) ||
    Boolean(restaurant.loadingAnimation) ||
    Boolean(kitchenMode) ||
    Boolean(serviceMode) ||
    salonEnabled !== undefined ||
    deliveryEnabled === true ||
    operationalSetupCompleted === true ||
    Boolean(coverDisplayMode) ||
    Boolean(kioskDineInImageUrl) ||
    Boolean(kioskTakeawayImageUrl) ||
    Boolean(kioskMenuMode) ||
    Number(kioskStationCount || 0) > 0 ||
    Boolean(paymentProvider) ||
    Boolean(paymentProviderStatus) ||
    Boolean(paymentAccountReference) ||
    Boolean(paymentAccountCbu) ||
    Boolean(paymentAccountAlias) ||
    Boolean(paymentLocationCity) ||
    Boolean(paymentLocationState) ||
    Boolean(paymentLocationLatitude) ||
    Boolean(paymentLocationLongitude) ||
    Boolean(paymentLocationReference) ||
    kioskNativeQrReady === true ||
    Number(commissionRate || 0) > 0 ||
    Number(channelRates.manualOrderCommissionRate || 0) > 0 ||
    Number(channelRates.menuQrCommissionRate || 0) > 0 ||
    Number(channelRates.kioskCommissionRate || 0) > 0 ||
    Number(channelRates.iaCommissionRate || 0) > 0 ||
    stockEnabled === false ||
    waiterEnabled === false ||
    aiEnabled === false ||
    Boolean(paymentSplitMode) ||
    Array.isArray(kioskPaymentMethods) ||
    Array.isArray(qrPaymentMethods);

  if (!hasRemoteSettings) {
    return null;
  }

  return {
    name: restaurant.name,
    phone: restaurant.phone,
    address: restaurant.address,
    description: restaurant.description,
    logoUrl: restaurant.logoUrl,
    logoCompactUrl: restaurant.logoCompactUrl,
    iconLogoUrl: restaurant.iconLogoUrl,
    loadingLogoUrl: restaurant.loadingLogoUrl,
    coverUrl: restaurant.coverUrl,
    primaryColor: restaurant.primaryColor,
    secondaryColor: restaurant.secondaryColor,
    fontFamily: restaurant.fontFamily,
    loadingAnimation: restaurant.loadingAnimation,
    welcomeMessage: restaurant.welcomeMessage,
    kitchenMode,
    serviceMode,
    salonEnabled:
      salonEnabled === undefined ? serviceMode !== "kiosk" : salonEnabled !== false,
    deliveryEnabled: deliveryEnabled === true,
    operationalSetupCompleted: operationalSetupCompleted === true,
    stockEnabled: normalizeOperationalFeatureEnabled(stockEnabled),
    waiterEnabled: normalizeOperationalFeatureEnabled(waiterEnabled),
    aiEnabled: normalizeOperationalFeatureEnabled(aiEnabled),
    coverDisplayMode,
    kioskDineInImageUrl,
    qrMenuStyle: readLegacyBranchSetting(restaurant, "qrMenuStyle", "qr_menu_style"),
    qrCategoryOrder: readLegacyBranchSetting(restaurant, "qrCategoryOrder", "qr_category_order"),
    qrSubcategoryOrder: readLegacyBranchSetting(restaurant, "qrSubcategoryOrder", "qr_subcategory_order"),
    kioskTakeawayImageUrl,
    kioskMenuMode,
    kioskStationCount,
    paymentProvider,
    paymentProviderStatus,
    paymentAccountReference,
    paymentAccountCbu,
    paymentAccountAlias,
    paymentLocationCity,
    paymentLocationState,
    paymentLocationLatitude,
    paymentLocationLongitude,
    paymentLocationReference,
    kioskNativeQrReady: kioskNativeQrReady === true,
    commissionRate: channelRates.manualOrderCommissionRate,
    manualOrderCommissionRate: channelRates.manualOrderCommissionRate,
    menuQrCommissionRate: channelRates.menuQrCommissionRate,
    kioskCommissionRate: channelRates.kioskCommissionRate,
    iaCommissionRate: channelRates.iaCommissionRate,
    paymentSplitMode,
    kioskPaymentMethods,
    qrPaymentMethods,
  };
};

export const mergeRemoteBranchIntoProfile = (profile = {}, remoteBranchProfile = {}) => {
  const currentBranding = normalizeRestaurantBranding(profile?.branding || {});
  const remoteLogoUrl = remoteBranchProfile?.logoUrl || "";
  const remoteCoverUrl = remoteBranchProfile?.coverUrl;
  const nextServiceMode = normalizeServiceMode(
    remoteBranchProfile?.serviceMode ?? profile?.serviceMode
  );
  const nextOperatingHours = normalizeHorarioDetallado(
    remoteBranchProfile?.horarioDetallado ?? profile?.horarioDetallado
  );

  return {
    ...profile,
    nombre: remoteBranchProfile?.name ?? profile?.nombre,
    telefono: remoteBranchProfile?.phone ?? profile?.telefono,
    direccion: remoteBranchProfile?.address ?? profile?.direccion,
    descripcion: remoteBranchProfile?.description ?? profile?.descripcion,
    horarioDetallado: nextOperatingHours,
    horario: buildHorarioResumen(nextOperatingHours),
    imagenPortada: remoteCoverUrl ?? profile?.imagenPortada,
    coverUrl: remoteCoverUrl ?? profile?.coverUrl ?? profile?.imagenPortada ?? "",
    coverDisplayMode: normalizeCoverDisplayMode(
      remoteBranchProfile?.coverDisplayMode ?? profile?.coverDisplayMode
    ),
    kioskDineInImageUrl:
      remoteBranchProfile?.kioskDineInImageUrl ?? profile?.kioskDineInImageUrl ?? "",
    kioskTakeawayImageUrl:
      remoteBranchProfile?.kioskTakeawayImageUrl ??
      profile?.kioskTakeawayImageUrl ??
      "",
    mensajeBienvenida:
      remoteBranchProfile?.welcomeMessage ?? profile?.mensajeBienvenida,
    kitchenMode: normalizeKitchenModeForServiceMode(
      nextServiceMode,
      remoteBranchProfile?.kitchenMode ?? profile?.kitchenMode
    ),
    serviceMode: nextServiceMode,
    salonEnabled:
      nextServiceMode === "kiosk"
        ? false
        : normalizeOperationalFeatureEnabled(
            remoteBranchProfile?.salonEnabled ?? profile?.salonEnabled
          ),
    deliveryEnabled:
      (remoteBranchProfile?.deliveryEnabled ?? profile?.deliveryEnabled) === true,
    operationalSetupCompleted:
      (remoteBranchProfile?.operationalSetupCompleted ??
        profile?.operationalSetupCompleted) === true,
    stockEnabled: normalizeOperationalFeatureEnabled(
      remoteBranchProfile?.stockEnabled ?? profile?.stockEnabled
    ),
    waiterEnabled: normalizeOperationalFeatureEnabled(
      remoteBranchProfile?.waiterEnabled ?? profile?.waiterEnabled
    ),
    aiEnabled: normalizeOperationalFeatureEnabled(
      remoteBranchProfile?.aiEnabled ?? profile?.aiEnabled
    ),
    kioskMenuMode: normalizeKioskMenuMode(
      remoteBranchProfile?.kioskMenuMode ?? profile?.kioskMenuMode
    ),
    qrMenuStyle: normalizeQrMenuStyle(remoteBranchProfile?.qrMenuStyle ?? profile?.qrMenuStyle),
    qrCategoryOrder: normalizeQrCategoryOrder(remoteBranchProfile?.qrCategoryOrder ?? profile?.qrCategoryOrder),
    qrSubcategoryOrder: normalizeQrSubcategoryOrder(remoteBranchProfile?.qrSubcategoryOrder ?? profile?.qrSubcategoryOrder),
    kioskStationCount: normalizeKioskStationCount(
      remoteBranchProfile?.kioskStationCount ?? profile?.kioskStationCount
    ),
    paymentProvider: normalizePaymentProvider(
      remoteBranchProfile?.paymentProvider ?? profile?.paymentProvider
    ),
    paymentProviderStatus: normalizePaymentProviderStatus(
      remoteBranchProfile?.paymentProviderStatus ?? profile?.paymentProviderStatus
    ),
    paymentAccountReference: sanitizePaymentAccountReference(
      remoteBranchProfile?.paymentAccountReference ??
        profile?.paymentAccountReference ??
        ""
    ),
    paymentAccountCbu:
      remoteBranchProfile?.paymentAccountCbu ?? profile?.paymentAccountCbu ?? "",
    paymentAccountAlias:
      remoteBranchProfile?.paymentAccountAlias ?? profile?.paymentAccountAlias ?? "",
    paymentLocationCity:
      remoteBranchProfile?.paymentLocationCity ?? profile?.paymentLocationCity ?? "",
    paymentLocationState:
      remoteBranchProfile?.paymentLocationState ?? profile?.paymentLocationState ?? "",
    paymentLocationLatitude:
      remoteBranchProfile?.paymentLocationLatitude ??
      profile?.paymentLocationLatitude ??
      "",
    paymentLocationLongitude:
      remoteBranchProfile?.paymentLocationLongitude ??
      profile?.paymentLocationLongitude ??
      "",
    paymentLocationReference:
      remoteBranchProfile?.paymentLocationReference ??
      profile?.paymentLocationReference ??
      "",
    kioskNativeQrReady:
      remoteBranchProfile?.kioskNativeQrReady ??
      profile?.kioskNativeQrReady ??
      false,
    commissionRate: normalizeCommissionRate(
      remoteBranchProfile?.manualOrderCommissionRate ??
        remoteBranchProfile?.commissionRate ??
        profile?.manualOrderCommissionRate ??
        profile?.commissionRate
    ),
    manualOrderCommissionRate: normalizeCommissionRate(
      remoteBranchProfile?.manualOrderCommissionRate ??
        remoteBranchProfile?.commissionRate ??
        profile?.manualOrderCommissionRate ??
        profile?.commissionRate
    ),
    menuQrCommissionRate: normalizeCommissionRate(
      remoteBranchProfile?.menuQrCommissionRate ??
        profile?.menuQrCommissionRate ??
        profile?.commissionRate
    ),
    kioskCommissionRate: normalizeCommissionRate(
      remoteBranchProfile?.kioskCommissionRate ??
        profile?.kioskCommissionRate ??
        profile?.commissionRate
    ),
    iaCommissionRate: normalizeCommissionRate(
      remoteBranchProfile?.iaCommissionRate ??
        profile?.iaCommissionRate ??
        profile?.commissionRate
    ),
    paymentSplitMode: normalizePaymentSplitMode(
      remoteBranchProfile?.paymentSplitMode ?? profile?.paymentSplitMode
    ),
    kioskPaymentMethods: normalizePaymentMethods(
      remoteBranchProfile?.kioskPaymentMethods ?? profile?.kioskPaymentMethods,
      "kiosk"
    ),
    qrPaymentMethods: normalizePaymentMethods(
      remoteBranchProfile?.qrPaymentMethods ?? profile?.qrPaymentMethods,
      "qr"
    ),
    branding: normalizeRestaurantBranding({
      ...currentBranding,
      logoPrimaryUrl: remoteLogoUrl || currentBranding.logoPrimaryUrl || "",
      logoCompactUrl:
        remoteBranchProfile?.logoCompactUrl ||
        remoteLogoUrl ||
        currentBranding.logoCompactUrl ||
        currentBranding.logoPrimaryUrl ||
        "",
      iconLogoUrl:
        remoteBranchProfile?.iconLogoUrl ||
        remoteBranchProfile?.logoCompactUrl ||
        remoteLogoUrl ||
        currentBranding.iconLogoUrl ||
        currentBranding.logoCompactUrl ||
        currentBranding.logoPrimaryUrl ||
        "",
      loadingLogoUrl:
        remoteBranchProfile?.loadingLogoUrl ||
        remoteBranchProfile?.iconLogoUrl ||
        remoteBranchProfile?.logoCompactUrl ||
        remoteLogoUrl ||
        currentBranding.loadingLogoUrl ||
        currentBranding.iconLogoUrl ||
        currentBranding.logoCompactUrl ||
        currentBranding.logoPrimaryUrl ||
        "",
      primaryColor:
        remoteBranchProfile?.primaryColor || currentBranding.primaryColor || "",
      secondaryColor:
        remoteBranchProfile?.secondaryColor || currentBranding.secondaryColor || "",
      fontFamily: remoteBranchProfile?.fontFamily || currentBranding.fontFamily || "",
      loadingAnimation:
        remoteBranchProfile?.loadingAnimation ||
        currentBranding.loadingAnimation ||
        "spin",
    }),
  };
};

export const applyLocalCoverDisplayModePreference = (profile = {}, ...sources) => {
  const preferredSource = sources.find(
    (source) =>
      source &&
      source.coverDisplayMode !== undefined &&
      source.coverDisplayMode !== null &&
      `${source.coverDisplayMode}`.trim() !== ""
  );

  if (!preferredSource) {
    return profile;
  }

  return {
    ...profile,
    coverDisplayMode: normalizeCoverDisplayMode(preferredSource.coverDisplayMode),
  };
};

export const mergeRemoteBranchIntoProfilePreservingLocalPreferences = (
  profile = {},
  remoteBranchProfile = {}
) =>
  applyLocalCoverDisplayModePreference(
    mergeRemoteBranchIntoProfile(profile, remoteBranchProfile),
    profile
  );

export const getRestaurantProfile = (restaurant) => {
  const storageKey = buildRestaurantProfileStorageKey(restaurant?.id);
  const stateStorageKey = buildRestaurantProfileStateStorageKey(restaurant?.id);
  const remoteBranchProfile = extractRemoteBranchProfileFromRestaurant(restaurant);
  let persistedState = {};

  try {
    const localState = readStorageObject(
      window.localStorage,
      stateStorageKey,
      "No pudimos leer el estado liviano del restaurante en localStorage:"
    );
    const sessionState = readStorageObject(
      window.sessionStorage,
      stateStorageKey,
      "No pudimos leer el estado liviano del restaurante en sessionStorage:"
    );
    persistedState = stripRemoteOperationalProfileFields(
      {
        ...localState,
        ...sessionState,
      },
      remoteBranchProfile
    );

    const saved = window.localStorage.getItem(storageKey);
    if (!saved) {
      return applyLocalCoverDisplayModePreference(
        mergeRemoteBranchIntoProfile(
          {
            ...createDefaultProfile(restaurant),
            ...persistedState,
            serviceMode: normalizeServiceMode(
              persistedState?.serviceMode || createDefaultProfile(restaurant).serviceMode
            ),
            kioskMenuMode: normalizeKioskMenuMode(
              persistedState?.kioskMenuMode || createDefaultProfile(restaurant).kioskMenuMode
            ),
            kioskStationCount: normalizeKioskStationCount(
              persistedState?.kioskStationCount || createDefaultProfile(restaurant).kioskStationCount
            ),
            kitchenTicketPaper: normalizeKitchenTicketPaper(
              persistedState?.kitchenTicketPaper || createDefaultProfile(restaurant).kitchenTicketPaper
            ),
            salonEnabled:
              normalizeServiceMode(
                persistedState?.serviceMode || createDefaultProfile(restaurant).serviceMode
              ) === "kiosk"
                ? false
                : normalizeOperationalFeatureEnabled(
                    persistedState?.salonEnabled ??
                      createDefaultProfile(restaurant).salonEnabled
                  ),
            deliveryEnabled:
              (persistedState?.deliveryEnabled ??
                createDefaultProfile(restaurant).deliveryEnabled) === true,
            operationalSetupCompleted:
              (persistedState?.operationalSetupCompleted ??
                createDefaultProfile(restaurant).operationalSetupCompleted) === true,
            stockEnabled: normalizeOperationalFeatureEnabled(
              persistedState?.stockEnabled ?? createDefaultProfile(restaurant).stockEnabled
            ),
            waiterEnabled: normalizeOperationalFeatureEnabled(
              persistedState?.waiterEnabled ?? createDefaultProfile(restaurant).waiterEnabled
            ),
            aiEnabled: normalizeOperationalFeatureEnabled(
              persistedState?.aiEnabled ?? createDefaultProfile(restaurant).aiEnabled
            ),
            coverDisplayMode: normalizeCoverDisplayMode(
              persistedState?.coverDisplayMode || createDefaultProfile(restaurant).coverDisplayMode
            ),
            branding: normalizeRestaurantBranding(
              persistedState?.branding || createDefaultProfile(restaurant).branding
            ),
          },
          remoteBranchProfile
        ),
        persistedState
      );
    }

    const parsed = stripRemoteOperationalProfileFields(
      JSON.parse(saved),
      remoteBranchProfile
    );
    const baseProfile = createDefaultProfile(restaurant);
    const horarioDetallado = normalizeHorarioDetallado(
      parsed?.horarioDetallado || baseProfile.horarioDetallado
    );

    return applyLocalCoverDisplayModePreference(
      mergeRemoteBranchIntoProfile(
        {
          ...baseProfile,
          ...persistedState,
          ...parsed,
          kitchenTicketPaper: normalizeKitchenTicketPaper(
            parsed?.kitchenTicketPaper ||
              persistedState?.kitchenTicketPaper ||
              baseProfile.kitchenTicketPaper
          ),
          serviceMode: normalizeServiceMode(
            parsed?.serviceMode || persistedState?.serviceMode || baseProfile.serviceMode
          ),
          salonEnabled:
            normalizeServiceMode(
              parsed?.serviceMode || persistedState?.serviceMode || baseProfile.serviceMode
            ) === "kiosk"
              ? false
              : normalizeOperationalFeatureEnabled(
                  parsed?.salonEnabled ??
                    persistedState?.salonEnabled ??
                    baseProfile.salonEnabled
                ),
          deliveryEnabled:
            (parsed?.deliveryEnabled ??
              persistedState?.deliveryEnabled ??
              baseProfile.deliveryEnabled) === true,
          operationalSetupCompleted:
            (parsed?.operationalSetupCompleted ??
              persistedState?.operationalSetupCompleted ??
              baseProfile.operationalSetupCompleted) === true,
          stockEnabled: normalizeOperationalFeatureEnabled(
            parsed?.stockEnabled ??
              persistedState?.stockEnabled ??
              baseProfile.stockEnabled
          ),
          waiterEnabled: normalizeOperationalFeatureEnabled(
            parsed?.waiterEnabled ??
              persistedState?.waiterEnabled ??
              baseProfile.waiterEnabled
          ),
          aiEnabled: normalizeOperationalFeatureEnabled(
            parsed?.aiEnabled ?? persistedState?.aiEnabled ?? baseProfile.aiEnabled
          ),
          kioskMenuMode: normalizeKioskMenuMode(
            parsed?.kioskMenuMode || persistedState?.kioskMenuMode || baseProfile.kioskMenuMode
          ),
          kioskStationCount: normalizeKioskStationCount(
            parsed?.kioskStationCount ||
              persistedState?.kioskStationCount ||
              baseProfile.kioskStationCount
          ),
          kitchenMode: normalizeKitchenModeForServiceMode(
            normalizeServiceMode(
              parsed?.serviceMode || persistedState?.serviceMode || baseProfile.serviceMode
            ),
            parsed?.kitchenMode || persistedState?.kitchenMode || baseProfile.kitchenMode
          ),
          coverDisplayMode: normalizeCoverDisplayMode(
            parsed?.coverDisplayMode ||
              persistedState?.coverDisplayMode ||
              baseProfile.coverDisplayMode
          ),
          branding: normalizeRestaurantBranding(
            parsed?.branding || persistedState?.branding || baseProfile.branding
          ),
          horarioDetallado,
          horario: buildHorarioResumen(horarioDetallado),
        },
        remoteBranchProfile
      ),
      parsed,
      persistedState
    );
  } catch (error) {
    console.error("No pudimos leer el perfil del restaurante:", error);
    return applyLocalCoverDisplayModePreference(
      mergeRemoteBranchIntoProfile(
        {
          ...createDefaultProfile(restaurant),
          ...persistedState,
          serviceMode: normalizeServiceMode(
            persistedState?.serviceMode || createDefaultProfile(restaurant).serviceMode
          ),
          kioskMenuMode: normalizeKioskMenuMode(
            persistedState?.kioskMenuMode || createDefaultProfile(restaurant).kioskMenuMode
          ),
          kioskStationCount: normalizeKioskStationCount(
            persistedState?.kioskStationCount || createDefaultProfile(restaurant).kioskStationCount
          ),
          kitchenTicketPaper: normalizeKitchenTicketPaper(
            persistedState?.kitchenTicketPaper || createDefaultProfile(restaurant).kitchenTicketPaper
          ),
          salonEnabled:
            normalizeServiceMode(
              persistedState?.serviceMode || createDefaultProfile(restaurant).serviceMode
            ) === "kiosk"
              ? false
              : normalizeOperationalFeatureEnabled(
                  persistedState?.salonEnabled ??
                    createDefaultProfile(restaurant).salonEnabled
                ),
          deliveryEnabled:
            (persistedState?.deliveryEnabled ??
              createDefaultProfile(restaurant).deliveryEnabled) === true,
          operationalSetupCompleted:
            (persistedState?.operationalSetupCompleted ??
              createDefaultProfile(restaurant).operationalSetupCompleted) === true,
          stockEnabled: normalizeOperationalFeatureEnabled(
            persistedState?.stockEnabled ?? createDefaultProfile(restaurant).stockEnabled
          ),
          waiterEnabled: normalizeOperationalFeatureEnabled(
            persistedState?.waiterEnabled ?? createDefaultProfile(restaurant).waiterEnabled
          ),
          aiEnabled: normalizeOperationalFeatureEnabled(
            persistedState?.aiEnabled ?? createDefaultProfile(restaurant).aiEnabled
          ),
          coverDisplayMode: normalizeCoverDisplayMode(
            persistedState?.coverDisplayMode || createDefaultProfile(restaurant).coverDisplayMode
          ),
          branding: normalizeRestaurantBranding(
            persistedState?.branding || createDefaultProfile(restaurant).branding
          ),
        },
        remoteBranchProfile
      ),
      persistedState
    );
  }
};

export const emitRestaurantProfileUpdate = (restaurant, profile) => {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(RESTAURANT_PROFILE_UPDATED_EVENT, {
      detail: {
        restaurantId: restaurant?.id || "default",
        profile,
      },
    })
  );
};

const buildProfileWithoutHeavyImages = (profile) => ({
  ...profile,
  imagenPortada: "",
  coverUrl: "",
  kioskDineInImageUrl: "",
  kioskTakeawayImageUrl: "",
  branding: normalizeRestaurantBranding({
    ...(profile?.branding || {}),
    logoPrimaryUrl: "",
    logoCompactUrl:
      profile?.branding?.logoCompactUrl || profile?.branding?.logoPrimaryUrl || "",
    iconLogoUrl: "",
    loadingLogoUrl: "",
  }),
});

const buildEssentialProfileState = (profile) => ({
  qrMenuStyle: normalizeQrMenuStyle(profile?.qrMenuStyle),
  qrCategoryOrder: normalizeQrCategoryOrder(profile?.qrCategoryOrder),
  qrSubcategoryOrder: normalizeQrSubcategoryOrder(profile?.qrSubcategoryOrder),
  kitchenMode: normalizeKitchenModeForServiceMode(
    profile?.serviceMode,
    profile?.kitchenMode
  ),
  kitchenTicketPaper: normalizeKitchenTicketPaper(profile?.kitchenTicketPaper),
  serviceMode: normalizeServiceMode(profile?.serviceMode),
  salonEnabled:
    normalizeServiceMode(profile?.serviceMode) === "kiosk"
      ? false
      : normalizeOperationalFeatureEnabled(profile?.salonEnabled),
  deliveryEnabled: profile?.deliveryEnabled === true,
  wePedidosEnabled: profile?.wePedidosEnabled === true,
  operationalSetupCompleted: profile?.operationalSetupCompleted === true,
  stockEnabled: normalizeOperationalFeatureEnabled(profile?.stockEnabled),
  waiterEnabled: normalizeOperationalFeatureEnabled(profile?.waiterEnabled),
  aiEnabled: normalizeOperationalFeatureEnabled(profile?.aiEnabled),
  coverDisplayMode: normalizeCoverDisplayMode(profile?.coverDisplayMode),
  kioskDineInImageUrl: profile?.kioskDineInImageUrl || "",
  kioskTakeawayImageUrl: profile?.kioskTakeawayImageUrl || "",
  kioskMenuMode: normalizeKioskMenuMode(profile?.kioskMenuMode),
  kioskStationCount: normalizeKioskStationCount(profile?.kioskStationCount),
  paymentProvider: normalizePaymentProvider(profile?.paymentProvider),
  paymentProviderStatus: normalizePaymentProviderStatus(profile?.paymentProviderStatus),
  paymentAccountReference: sanitizePaymentAccountReference(
    profile?.paymentAccountReference
  ),
  paymentAccountCbu: profile?.paymentAccountCbu || "",
  paymentAccountAlias: profile?.paymentAccountAlias || "",
  paymentLocationCity: profile?.paymentLocationCity || "",
  paymentLocationState: profile?.paymentLocationState || "",
  paymentLocationLatitude: profile?.paymentLocationLatitude || "",
  paymentLocationLongitude: profile?.paymentLocationLongitude || "",
  paymentLocationReference: profile?.paymentLocationReference || "",
  kioskNativeQrReady: profile?.kioskNativeQrReady === true,
  commissionRate: normalizeCommissionRate(
    profile?.manualOrderCommissionRate ?? profile?.commissionRate
  ),
  manualOrderCommissionRate: normalizeCommissionRate(
    profile?.manualOrderCommissionRate ?? profile?.commissionRate
  ),
  menuQrCommissionRate: normalizeCommissionRate(
    profile?.menuQrCommissionRate ?? profile?.commissionRate
  ),
  kioskCommissionRate: normalizeCommissionRate(
    profile?.kioskCommissionRate ?? profile?.commissionRate
  ),
  iaCommissionRate: normalizeCommissionRate(
    profile?.iaCommissionRate ?? profile?.commissionRate
  ),
  paymentSplitMode: normalizePaymentSplitMode(profile?.paymentSplitMode),
  kioskPaymentMethods: normalizePaymentMethods(profile?.kioskPaymentMethods, "kiosk"),
  qrPaymentMethods: normalizePaymentMethods(profile?.qrPaymentMethods, "qr"),
  mensajeBienvenida: profile?.mensajeBienvenida || "",
  branding: normalizeRestaurantBranding({
    ...(profile?.branding || {}),
    logoPrimaryUrl: "",
    logoCompactUrl:
      profile?.branding?.logoCompactUrl || profile?.branding?.logoPrimaryUrl || "",
    iconLogoUrl: "",
    loadingLogoUrl: "",
  }),
});

export const saveRestaurantProfile = (restaurant, profile) => {
  const storageKey = buildRestaurantProfileStorageKey(restaurant?.id);
  const stateStorageKey = buildRestaurantProfileStateStorageKey(restaurant?.id);
  const horarioDetallado = normalizeHorarioDetallado(profile?.horarioDetallado);
  const coverUrl = `${profile?.imagenPortada || profile?.coverUrl || ""}`.trim();
  const payload = {
    ...profile,
    qrMenuStyle: normalizeQrMenuStyle(profile?.qrMenuStyle),
    qrCategoryOrder: normalizeQrCategoryOrder(profile?.qrCategoryOrder),
    qrSubcategoryOrder: normalizeQrSubcategoryOrder(profile?.qrSubcategoryOrder),
    imagenPortada: coverUrl,
    coverUrl,
    serviceMode: normalizeServiceMode(profile?.serviceMode),
    kitchenMode: normalizeKitchenModeForServiceMode(
      profile?.serviceMode,
      profile?.kitchenMode
    ),
    kitchenTicketPaper: normalizeKitchenTicketPaper(profile?.kitchenTicketPaper),
    salonEnabled:
      normalizeServiceMode(profile?.serviceMode) === "kiosk"
        ? false
        : normalizeOperationalFeatureEnabled(profile?.salonEnabled),
    deliveryEnabled: profile?.deliveryEnabled === true,
    wePedidosEnabled: profile?.wePedidosEnabled === true,
    operationalSetupCompleted: profile?.operationalSetupCompleted === true,
    stockEnabled: normalizeOperationalFeatureEnabled(profile?.stockEnabled),
    waiterEnabled: normalizeOperationalFeatureEnabled(profile?.waiterEnabled),
    aiEnabled: normalizeOperationalFeatureEnabled(profile?.aiEnabled),
    coverDisplayMode: normalizeCoverDisplayMode(profile?.coverDisplayMode),
    kioskMenuMode: normalizeKioskMenuMode(profile?.kioskMenuMode),
    kioskStationCount: normalizeKioskStationCount(profile?.kioskStationCount),
    paymentProvider: normalizePaymentProvider(profile?.paymentProvider),
    paymentProviderStatus: normalizePaymentProviderStatus(profile?.paymentProviderStatus),
    paymentAccountReference: sanitizePaymentAccountReference(
      profile?.paymentAccountReference
    ),
    paymentAccountCbu: `${profile?.paymentAccountCbu || ""}`.trim(),
    paymentAccountAlias: `${profile?.paymentAccountAlias || ""}`.trim(),
    paymentLocationCity: `${profile?.paymentLocationCity || ""}`.trim(),
    paymentLocationState: `${profile?.paymentLocationState || ""}`.trim(),
    paymentLocationLatitude: `${profile?.paymentLocationLatitude || ""}`.trim(),
    paymentLocationLongitude: `${profile?.paymentLocationLongitude || ""}`.trim(),
    paymentLocationReference: `${profile?.paymentLocationReference || ""}`.trim(),
    kioskNativeQrReady: profile?.kioskNativeQrReady === true,
    commissionRate: normalizeCommissionRate(
      profile?.manualOrderCommissionRate ?? profile?.commissionRate
    ),
    manualOrderCommissionRate: normalizeCommissionRate(
      profile?.manualOrderCommissionRate ?? profile?.commissionRate
    ),
    menuQrCommissionRate: normalizeCommissionRate(
      profile?.menuQrCommissionRate ?? profile?.commissionRate
    ),
    kioskCommissionRate: normalizeCommissionRate(
      profile?.kioskCommissionRate ?? profile?.commissionRate
    ),
    iaCommissionRate: normalizeCommissionRate(
      profile?.iaCommissionRate ?? profile?.commissionRate
    ),
    paymentSplitMode: normalizePaymentSplitMode(profile?.paymentSplitMode),
    kioskPaymentMethods: normalizePaymentMethods(profile?.kioskPaymentMethods, "kiosk"),
    qrPaymentMethods: normalizePaymentMethods(profile?.qrPaymentMethods, "qr"),
    branding: normalizeRestaurantBranding(profile?.branding),
    horarioDetallado,
    horario: buildHorarioResumen(horarioDetallado),
  };

  const persistPayload = (nextPayload) => {
    window.localStorage.setItem(storageKey, JSON.stringify(nextPayload));
  };

  const essentialState = buildEssentialProfileState(payload);
  const persistEssentialState = (storage) => {
    if (!storage) {
      return;
    }

    storage.setItem(stateStorageKey, JSON.stringify(essentialState));
  };

  try {
    persistEssentialState(window.sessionStorage);
  } catch (stateError) {
    console.error(
      "No pudimos guardar el estado liviano del restaurante en sessionStorage:",
      stateError
    );
  }

  try {
    persistEssentialState(window.localStorage);
  } catch (stateError) {
    console.error(
      "No pudimos guardar el estado liviano del restaurante en localStorage:",
      stateError
    );
  }

  try {
    persistPayload(payload);
  } catch (error) {
    console.error("No pudimos guardar el perfil completo del restaurante:", error);
    const message = `${error?.message || ""}`.toLowerCase();
    const isQuotaError =
      message.includes("quota") ||
      message.includes("exceeded") ||
      error?.name === "QuotaExceededError";

    if (!isQuotaError) {
      throw new Error("No se pudo guardar la informacion del restaurante.");
    }

    try {
      window.localStorage.removeItem(storageKey);
      window.localStorage.removeItem(stateStorageKey);
    } catch (cleanupError) {
      console.error(
        "No pudimos limpiar la version pesada del perfil antes del fallback:",
        cleanupError
      );
    }

    try {
      persistEssentialState(window.localStorage);
      persistPayload(buildProfileWithoutHeavyImages(payload));
    } catch (fallbackError) {
      console.error(
        "No pudimos guardar ni siquiera el perfil liviano del restaurante:",
        fallbackError
      );
    }
  }

  emitRestaurantProfileUpdate(restaurant, payload);
  return payload;
};

export const getRestaurantBrandTheme = (restaurant) =>
  buildRestaurantBrandTheme(getRestaurantProfile(restaurant));
